<template>
  <div>
    <template v-if="editing">
      <div style="position: relative;">
        <table 
          class="table-fixed mat-table p-4"
        >
          <tr>
            <th class="mat-table-material">
              <div class="vform-label white d-flex justify-content-center">
                {{ $t("MATERIAL") }} {{ dirty ? "*" : "" }}
              </div>
            </th>
            <th class="mat-table-color">
              <div class="vform-label white d-flex justify-content-center">
                {{ $t("COLOR") }}
              </div>
            </th>
            <th class="mat-table-tiling">
              <div class="vform-label white d-flex justify-content-center">
                {{ $t("TILING") }}
              </div>
            </th>
            <th class="mat-table-delete">
              <div class="vform-label white mb-0 pb-0" />
            </th>
          </tr>
          <tr 
            v-if="rows.length === 0"
            class="p-0"
          >
            <td class="p-2">{{ $t("noMaterialSelected") }}</td>
          </tr>
          <tr/>
          <tr
            v-for="(item, index) in rows"
            :key="'mat-table-' + index"
            class="pb-2 mat-table-row"
          >
            <td class="mat-table-material pb-2">
              <material-previewer-row
                :item="item"
                :all-materials="getAllMaterials"
                @change="dirty = true"
              />
            </td>
            <td class="mat-table-color">
              <div class="mat-table-color-item">
                <color-picker-and-field
                  :field-background-color="item.color ? item.color : null"
                  @saveColor="(color) => updateColor(color, index)"
                  @unsetColor="updateColor(null, index)"
                />
              </div>
            </td>
            <td class="mat-table-tiling">
              <div class="d-flex justify-content-center mat-table-tiling-item">
                <vector-widget
                  :editing="true"
                  :hide-save-button="true"
                  :value="item.tiling"
                  class="mat-color-vector-widget pt-1"
                  type="v2"
                  @save="(value) => {updateTiling(value, index);}"
                  @update="(value) => {updateTiling(value, index);}"
                />
              </div>
            </td>
            <td
              class="mat-table-delete mat-table-delete-item clickable"
              @click="deleteRow(index)"
            >
              <div class="d-flex justify-content-center delete-button">
                <icon size="1" type="trash" />
              </div>
            </td>
          </tr>
        </table>
        <div v-if="isLoading" class="mat-table-blocker"/>
      </div>

      <div class="w-100" />
      <div
        v-if="!isLoading"
        class="settings-button switched-colors d-inline-block mr-2 mt-1"
        @click="addRow"
      >
        <icon type="plus" /> {{ $t("ADD") }}
      </div>
      <div
        v-if="showSaveButton"
        class="settings-button switched-colors d-inline-block"
        @click="save"
      >
        {{ $t("save") }}
      </div>
      <div
        v-if="getErrorMaterials"
        class="form-error mt-2"
      >
        {{ getErrorMaterials }}
      </div>
    </template>
    <template v-else>
      <div v-for="(row, index) in rows" :key="'mat-table-' + index">
        <div class="material-info-box mb-2">
          <div class="material-info-item material-info-materials">
            <div class="label mt-2">{{ $t("MATERIAL") }}</div>
            {{ getMaterialDisplayName(row.material) }}
          </div>
          <div class="material-info-item material-info-color">
            <div class="label mt-2">{{ $t("COLOR") }}</div>
            <div
              :class="['colormat-color-preview clickable align-items-center']"
              :style="{
                position: 'relative !important',
                backgroundColor: row.color ? row.color : 'transparent',
                border: '1px solid ' + (row.color ? row.color : '#fff'),
              }"
            />
          </div>
          <div class="material-info-item material-info-tiling">
            <div class="label mt-2">{{ $t("TILING") }}</div>
            {{ row.tiling }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/**
 * Component for displaying and editing a list of materials, each with an image, color and tiling.
 */
import ColorPickerAndField from "@/components/widgets/editors/ColorPickerAndField";
import VectorWidget from "@/components/widgets/editors/VectorWidget";
import Icon from "@/components/Icon";
import { mapGetters } from "vuex";
import MaterialPreviewerRow from "./MaterialPreviewerRow.vue";

export default {
  name: "MaterialPreviewer",
  components: {
    ColorPickerAndField,
    VectorWidget,
    Icon,
    MaterialPreviewerRow,
  },
  props: {
    value: { type: Array, default: () => [] },
    editing: { type: Boolean, default: true },
    showSaveButton: { type: Boolean, default: true },
    isLoading: { type: Boolean, default: false },
    defaultTiling: {
      type: Array,
      default: () => {
        return [1, 1];
      },
    },
  },
  data() {
    return {
      rows: [],
      loadingFlag: false,
      dirty: false, // Flag to track if the component has unsaved changes
    };
  },
  computed: {
    ...mapGetters(["getErrorMaterials", "getAllMaterials"]),
  },
  watch: {
    /**
     * Watches for changes in the 'value' prop.
     * loadingFlag is added to prevent recursive rerenders from occurring if we ever were to land
     * in a situation where the item was faulty and always empty. (Additional safety measure)
     */
    value: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (Array.isArray(newVal) && newVal.length === 0) {
          // MORE EMITS FOR THE EMIT GOD
          if (!this.loadingFlag) {
            this.loadingFlag = true;
            this.$emit("requestMaterials");
          }
          return;
        }
        this.loadingFlag = false;
        this.rows = JSON.parse(JSON.stringify(newVal));
      }
    }
  },
  methods: {
    resetDirty() {
      this.dirty = false;
    },
    /**
     * Retrieves the specified material's display name using its UUID.
     * @param uuid
     */
    getMaterialDisplayName(uuid) {
      if (!uuid) return "N / A";
      const matObject = this.getAllMaterials.find((x) => x.id === uuid);
      return matObject ? matObject.displayName : uuid;
    },
    updateTiling(size, index) {
      this.rows[index].tiling = size;
      this.dirty = true;
    },
    updateColor(color, index) {
      this.rows[index].color = color;
      this.dirty = true;
    },
    deleteRow(index) {
      this.rows.splice(index, 1);
      this.dirty = true;
    },
    /**
     * Adds a new row with defaults (empty material, default color, default tiling).
     */
    addRow() {
      this.rows.push({ 
        material: "", 
        color: "", 
        tiling: [...this.defaultTiling] // Clone the default tiling array
      });
      this.dirty = true;
    },
    getValues() {
      return this.rows;
    },
    /**
     * Emits a 'save' event with a JSON string of the current rows.
     * Catches and sets an error message on failure.
     * This method is used for saving the metavalues of the materials.
     */
    save() {
      try {
        const validRows = this.getValues();
        this.$emit("save", JSON.stringify(validRows));
      } catch (error) {
        console.error("Error in save method:", error);
        this.$store.commit("setErrorMaterials", "Error occurred while attempting to save materials");
      }
    },
  },
};
</script>
<style lang="scss" scoped>
/* STYLING FOR THE MATERIAL EDITOR */
.mat-table-blocker {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3); /* Semi-transparent background */
  z-index: 10; /* Ensure it's above the table content */
  display: flex;
  justify-content: center;
  align-items: center;
  animation: fadeIn 0.2s ease-in-out;
}

/* Fade-In Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.mat-table {
  td {
    padding-top: 8px;
    padding-bottom: 0;
  }
}


.mat-color-vector-widget {
  width: 120px;
  display: flex;
  margin-top: -15px;

  .vector-container {
    .form-group {
      width: 44%;
    }
  }

  .fieldset {
    padding: 0;
  }
}

.mat-table-row {
  border-radius: 0px 0px 5px 5px;
}

.mat-table-material {
  width: 60%;
}

.mat-table-color {
  width: 10%;
}

.mat-table-color-item {
  display: flex;
  justify-content: center;
  height: 7.3rem;
}

.mat-table-tiling {
  width: 25%;
}

.mat-table-tiling-item {
  height: 8.7rem;
}

.mat-table-delete {
  width: 5%;
}

.mat-table-delete-item {
  border: 1px solid #313030;
  background-color: #28272d;
}

/* STYLING FOR DISPLAYING MATERIAL INFORMATION */
.material-info-box {
  display: flex;
  flex-direction: row;
  padding: 10px;
  background-color: #28272d;
  border-radius: 8px;
}

.material-info-item {
  display: flex;
  flex-direction: column;
}

.material-info-materials {
  width: 62%;
}

.material-info-color,
.material-info-tiling {
  align-items: center;
  width: 19%;
}

/* MiSCELLANEOUS STYLING */

.colormat-color-preview {
  width: 15px;
  height: 15px;
  display: inline-block;
}

.mat-color-row {
  width: 100%;
  background-color: $panel-title-background-color;
  position: relative;
}

.material-select {
  height: 40px;
  margin: auto 0;
  width: 100%;
  font-size: 0.8rem;
}

.label {
  font-family: "Inter", sans-serif;
  font-weight: medium;
  margin-top: 15px;
}

.clr,
.del {
  width: 50px;
  min-width: auto;
}

.remove-color-button {
  height: 20px;
  margin-top: -0.179rem;
}

.mat-display {
  min-width: 200px;
}

.mini-text {
  font-size: 0.8rem;
}

@media (max-width: 1610px) {
  .mat-table-material {
    width: 55%;
  }
  .mat-table-color {
    width: 15%;
  }
  .mat-table-tiling {
    width: 25%;
  }
  .mat-table-delete {
    width: 5%;
  }
}

@media (max-width: 1350px) {
  .mat-table-color {
    width: 20%;
  }
}

// Medium devices (tablets, less than 992px)
@media (max-width: 991.98px) {
  .remove-color-button {
    margin-top: 0;
    font-size: 2em;
  }

  .mat-table-material {
    width: 45%;
  }

  .mat-table-color {
    width: 15%;
  }

  .mat-table-color-item {
    flex-direction: column;
    justify-content: start;
    align-items: center;
  }

  .mat-table-tiling {
    width: 15%;
  }

  .mat-table-tiling-item {
    height: 8.7rem;
  }

  .mat-table-delete {
    width: 5%;
  }
}

@media (max-width: 768px) {
  .remove-color-button {
    margin-top: 0;
    font-size: 2em;
  }

  .mat-table-material {
    width: 50%;
  }

  .mat-table-color {
    width: 15%;
  }

  .mat-table-color-item {
    flex-direction: column;
    justify-content: start;
    align-items: center;
  }

  .mat-table-tiling {
    width: 20%;
  }

  .mat-table-tiling-item {
    height: 8.7rem;
  }

  .mat-table-delete {
    width: 5%;
  }
}
</style>
